<template>
  <div class="results-view">
    <EmissionSuccess
      v-if="showEmissionSuccess"
      :quotes-response="quotesResponse"
      :policy-data="emissionSuccessData?.policyData"
      :documentos="emissionSuccessData?.documentos"
      :selected-plan-type="selectedPlanType"
      :selected-payment-method="selectedPaymentMethod"
      :selected-company="selectedCompany"
      :allow-emission="allowEmission"
      @back="handleBackFromSuccess"
      @edit-driver="handleEditDriver"
    />

    <EmissionForm
      v-else-if="showEmissionForm && emissionCompany"
      :quotes-response="selectedQuotesForEmission"
      :selected-plan-type="selectedPlanType"
      :selected-company="emissionCompany"
      :allow-emission="allowEmission"
      @close="showEmissionForm = false"
      @success="handleEmissionSuccess"
    />

    <div v-else>
      <ResultsHeader @close="handleCloseResults" />

      <div class="results-content-wrapper" :class="{ 'is-loading': resultsLoading }">
        <ResultsSummaryBar
          :quote-data="quoteData"
          :is-sending-email="isSendingEmail"
          :is-sending-whats-app="isSendingWhatsApp"
          :is-downloading="isDownloading"
          :can-share-or-download="canShareOrDownload"
          @edit-vehicle="handleEditVehicle"
          @edit-driver="handleEditDriver"
          @send-email="handleSendEmail"
          @send-whatsapp="handleSendWhatsApp"
          @download="handleDownload"
        />

        <ResultsComparison
          :columns="comparisonColumns"
          :rows="coverageRows"
          :selected-plan-type-label="selectedPlanTypeLabel"
          :selected-payment-method="selectedPaymentMethod"
          :allow-emission="allowEmission"
          :format-price="formatPrice"
          @image-error="handleImageError"
          @emitir="handleEmitir"
        >
          <template #toolbar>
            <ResultsFilters
              :selected-payment-method="selectedPaymentMethod"
              :selected-plan-type="selectedPlanType"
              :plan-type-options="planTypeOptions"
              @update:selected-payment-method="selectedPaymentMethod = $event"
              @update:selected-plan-type="selectedPlanType = $event"
              @payment-method-change="handlePaymentMethodChange"
              @plan-type-change="handlePlanTypeSegmentChange"
            />
          </template>
        </ResultsComparison>
      </div>

      <div class="results-footer">
        <div class="footer-links">
          <a href="#" class="footer-link" @click.prevent="handleOpenPrivacyDialog">Aviso de privacidad</a>
        </div>
      </div>

      <div v-if="resultsLoading" class="results-loading-overlay">
        <LoadingScreen />
      </div>
    </div>

    <PrivacyNoticeDialog v-model="showPrivacyDialog" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CotizaTodasResponse } from '@/types/quote.types';
import LoadingScreen from '@/components/LoadingScreen.vue';
import { useResultsView } from '@/composables/useResultsView';
import { buildComparisonColumns, buildCoverageComparison, type CompanyQuote } from '@/composables/resultsView/coverageComparison';
import EmissionForm from './EmissionForm.vue';
import EmissionSuccess from './EmissionSuccess.vue';
import PrivacyNoticeDialog from './PrivacyNoticeDialog.vue';
import ResultsFilters from './results/ResultsFilters.vue';
import ResultsHeader from './results/ResultsHeader.vue';
import ResultsComparison from './results/ResultsComparison.vue';
import ResultsSummaryBar from './results/ResultsSummaryBar.vue';
import type { EmittableCompany, ResultsViewProps } from './results/results-view.types';
import { isEmittableCompany } from './results/results-view.types';

const props = withDefaults(defineProps<ResultsViewProps>(), {
  isLoading: false,
  allowEmission: false,
});

const emit = defineEmits<{
  editVehicle: [];
  editDriver: [];
  quotesUpdated: [response: CotizaTodasResponse];
}>();

const {
  quoteData,
  quotesResponse,
  anaQuotes,
  chubbQuotes,
  isSendingEmail,
  isSendingWhatsApp,
  isDownloading,
  resultsLoading,
  selectedPaymentMethod,
  selectedPlanType,
  selectedCompany,
  canShareOrDownload,
  planTypeOptions,
  selectedPlanTypeLabel,
  companyCards,
  qualitasLogo,
  anaLogo,
  selectedQuotesForEmission,
  showEmissionForm,
  showEmissionSuccess,
  emissionSuccessData,
  showPrivacyDialog,
  handlePaymentMethodChange,
  handlePlanTypeSegmentChange,
  handleEditVehicle,
  handleEditDriver,
  handleCloseResults,
  formatPrice,
  handleImageError,
  handleEmitir,
  handleEmissionSuccess,
  handleBackFromSuccess,
  handleSendEmail,
  handleSendWhatsApp,
  handleDownload,
  handleOpenPrivacyDialog,
} = useResultsView(props, emit);

const allowEmission = props.allowEmission;

const coverageRows = computed(() => {
  const quoteFor = { QUALITAS: quotesResponse.value, ANA: anaQuotes.value, CHUBB: chubbQuotes.value };
  const quotes = companyCards.value
    .map((card) => ({ company: card.company, quote: quoteFor[card.company] }))
    .filter((entry): entry is CompanyQuote => Boolean(entry.quote));
  return buildCoverageComparison(quotes);
});

const comparisonColumns = computed(() =>
  buildComparisonColumns(companyCards.value, { QUALITAS: qualitasLogo.value, ANA: anaLogo.value }),
);

const emissionCompany = computed<EmittableCompany | null>(() =>
  isEmittableCompany(selectedCompany.value) ? selectedCompany.value : null,
);
</script>

<style src="@/styles/results-view.css"></style>
